import {
  ContextualTipId,
  useContextualTip,
  useContextualTipContext,
} from '@resistapp/client/contexts/contextual-tip-context';
import { OverviewChartConfiguration } from '@resistapp/client/utils/overview-chart-configurations';
import { MetricMode } from '@resistapp/common/types';
import { useEffect, useRef } from 'react';
import { ContextualTip } from '../tooltips/contextual-tips/contextual-tip';
import { InfoIconProps } from './info-icons-general';
import { ReductionInfoIcon } from './reduction-info-icon';
import { ResistanceIndexInfoIcon } from './resistance-index-info-icon';
import { RiskScoreInfoIcon } from './risk-score-info-icon';

interface Props {
  metricMode: MetricMode;
  infoIconProps?: InfoIconProps;
  /** Whether to show the contextual onboarding tip for this icon (defaults to false). */
  enableContextualTip?: boolean;
  activeOverviewConfiguration?: OverviewChartConfiguration;
  isMobile?: boolean;
}

export function LegendInfoIcon({
  metricMode,
  infoIconProps = {},
  activeOverviewConfiguration,
  enableContextualTip = false,
}: Props) {
  const tipId =
    metricMode === MetricMode.ARGI
      ? ContextualTipId.MAP_LEGEND_INFO_ARGI
      : metricMode === MetricMode.RISK
        ? ContextualTipId.MAP_LEGEND_INFO_RISK
        : ContextualTipId.MAP_LEGEND_INFO_REDUCTION;
  const iconRef = useRef<HTMLSpanElement>(null);
  const { setTipEnabled } = useContextualTipContext();
  useEffect(() => {
    setTipEnabled(tipId, enableContextualTip);
    // Disable on unmount to avoid carrying over to other views
    return () => {
      setTipEnabled(tipId, false);
    };
  }, [tipId, enableContextualTip, setTipEnabled]);
  const {
    visible: hookVisible,
    message,
    dismiss,
    markInteraction,
    animationDelay,
    showDismiss,
  } = useContextualTip(tipId, metricMode);

  const visible = enableContextualTip && hookVisible;

  // Merge info icon props with interaction handler so that metric icons
  // can notify the contextual tip logic when the user interacts either via
  // click or hover.
  const interactionProps: InfoIconProps = { ...infoIconProps, onInteraction: markInteraction };

  // First just render the info icon directly with interaction props
  const infoIcon = (() => {
    switch (metricMode) {
      case MetricMode.RISK:
        return <RiskScoreInfoIcon {...interactionProps} />;
      case MetricMode.ARGI:
        return <ResistanceIndexInfoIcon {...interactionProps} />;
      case MetricMode.REDUCTION:
        return <ReductionInfoIcon {...interactionProps} activeOverviewConfiguration={activeOverviewConfiguration} />;
      default:
        return null;
    }
  })();

  return (
    <>
      <span ref={iconRef} style={{ display: 'inline-block' }}>
        {infoIcon}
      </span>
      {visible && (
        <ContextualTip
          anchorRef={iconRef}
          message={message || ''}
          onDismiss={dismiss}
          placement="left"
          animationDelay={animationDelay}
          showDismiss={showDismiss}
        />
      )}
    </>
  );
}
